<template>
  <template v-if="isDataLoaded">
    <custom-header></custom-header>
    <main class="main">
      <div class="container">
        <section v-if="isDataLoaded" :class="[!auctionStore.user?.is_admin ? 'live-auction' : 'live-auction-seller']">
          <LotMainSeller v-if="auctionStore.user?.is_admin"></LotMainSeller>
          <LotMainInfo v-else></LotMainInfo>
          <Catalog></Catalog>
          <ActionsTab></ActionsTab>
        </section>
      </div>
    </main>
  </template>
  <Preloader v-else class="load-app"></Preloader>
  <hot-keys-service v-if="isDataLoaded && auctionStore.user?.is_admin"></hot-keys-service>
  <live-alert-notification />
  <Toast />
</template>

<script lang="ts" setup>
  import { computed, inject, onMounted, reactive, ref, watch } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { updateAuctionListeners, removeAllListeners } from "@/utils/websockets";
  import Catalog from "@/components/Catalog.vue";
  import LotMainInfo from "@/components/LotMainInfo.vue";
  import CustomHeader from "@/components/layouts/CustomHeader.vue";
  import ActionsTab from "@/components/action-block/ActionsTab.vue";
  import Preloader from "@/components/_partials/Preloader.vue";
  import LotMainSeller from "@/components/seller/LotMainSeller.vue";
  import apiClient from "@/services/api";
  import Toast from "primevue/toast";
  import { useToast } from "primevue/usetoast";
  import HotKeysService from "@/components/seller/HotKeysService.vue";
  import LiveAlertNotification from "@/components/_partials/LiveAlertNotification.vue";
  import {trans} from "@/utils/trans";

  const auctionStore = useAuctionStore();
  const toast = useToast();
  const emitter = inject("emitter");

  const isDataLoaded = computed(() => auctionStore.translations && auctionStore.auction?.id);
  const changeStatusTimer = ref<null | number>(null);
  const state = reactive({
    alert: {
      intervalId: null as number | null,
      alertMessage: "" as string | null,
      leftBefore: null as number | null
    }
  });

  watch(
    () => auctionStore.pageIsActive,
    (nv, ov) => {
      if (auctionStore.auction.id && !auctionStore.user?.is_admin) {
        if (changeStatusTimer.value) {
          clearTimeout(changeStatusTimer.value);
          changeStatusTimer.value = null;
        }
        if (ov !== nv) {
          changeStatusTimer.value = setTimeout(() => {
            const status = nv ? "active" : "inactive";
            apiClient.post(`/live/user/${auctionStore.auction.id}/${status}`);
          }, 2000);
        }
      }
    }
  );

  onMounted(async () => {
    addPageStatusListeners();

    window.Echo.connector.pusher.connection.bind("connecting", () => {
      console.log("connecting...");
      removeAllListeners();
    });
    window.Echo.connector.pusher.connection.bind("connected", (payload: any) => {
      console.log("pusher connected!", payload);
      loadAuction();
    });

    auctionStore.initFilter();
    auctionStore.fetchMenu();
  });

  const loadAuction = async () => {
    await auctionStore.fetchAuction().then(() => {
      setTitle();

      updateAuctionListeners(toast, emitter);
      auctionStore.fetchLots();

      apiClient.post(`/live/user/${auctionStore.auction.id}/active`);
    });
  };

  const addPageStatusListeners = () => {
    document.addEventListener("visibilitychange", () => {
      auctionStore.$patch({
        pageIsActive: !document.hidden
      });
    });

    setInterval(() => {
      auctionStore.$patch({
        pageIsActive: document.hasFocus()
      });
    }, 1000);
  };

  watch(
    () => auctionStore.pageIsActive,
    (nv, ov) => {
      if (auctionStore.auction.id && !auctionStore.user?.is_admin) {
        if (changeStatusTimer.value) {
          clearTimeout(changeStatusTimer.value);
          changeStatusTimer.value = null;
        }
        if (ov !== nv) {
          changeStatusTimer.value = setTimeout(() => {
            const status = nv ? "active" : "inactive";
            apiClient.post(`/live/user/${auctionStore.auction.id}/${status}`);
          }, 2000);
        }
      }
      if (ov !== nv && nv && state.alert.intervalId) {
        clearInterval(state.alert.intervalId);
        state.alert.leftBefore = null;
        state.alert.alertMessage = null;
        setTitle();
      }
    }
  );

  emitter?.on("auction:lot-alert", (data: { content: string }) => {
    if (!auctionStore.pageIsActive) {
      state.alert.alertMessage = data.content;

      if (state.alert.intervalId) {
        clearInterval(state.alert.intervalId);
      }

      state.alert.intervalId = setInterval(() => {
        if (document.title === state.alert.alertMessage) {
          setTitle();
        } else if (state.alert.alertMessage) {
          document.title = state.alert.alertMessage;
        }
      }, 1000);
    }
  });

  const setTitle = () => {
    document.title = trans("live.common.title", {
      title: auctionStore.auction.title,
      app_name: process.env.VUE_APP_APP_NAME
    });
  };
</script>
